import { Box, Typography } from '@mui/material';
import { employeeDetailService } from '@services/private/employee-management/employee-detail.service';
import { is200xRange } from '@utils/utilities';
import { ROUTES } from 'constants/routes';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import Button from 'shared/components/widgets/button';
import FieldBox from 'shared/components/widgets/field-box';
import FieldBoxProperty from 'shared/components/widgets/field-box-property';
import { AdminConfigEnv } from 'types';

type EmployeeData = {
  adminConfigEnv: AdminConfigEnv;
  staffMstDetail: {
    joinTp: 'google' | 'markany' | 'kakao';
    regDt: string;
    regName: string;
    regTp: 'admin' | 'staff' | 'user';
    updDt: string;
    updName: string;
    updTp: string;
    userEmail: string;
    userName: string;
    userSeq: string;
    userSt: '10' | '50' | '80' | '90';
    userUid: string;
  } | null;
};

const EmployeeManagementDetail = ({ userSeq }) => {
  const [isLoading, setLoading] = useState<boolean>(false)
  const [employeeData, setEmployeeData] = useState<EmployeeData>({
    adminConfigEnv: null,
    staffMstDetail: null,
  });

  const getEmployeeManagementDetail = async () => {
    const { response, statusCode } = await employeeDetailService(userSeq);

    if (response?.code === '1' && is200xRange(statusCode)) {
      handleListData([
        { key: 'adminConfigEnv', value: response?.result?.adminConfigEnv },
        { key: 'staffMstDetail', value: response?.result?.staffMstDetail },
      ]);
    }
  };

  const handleListData = (params) => {
    setEmployeeData((prevListData) => {
      const newListData = { ...prevListData };
      params.forEach((item) => {
        newListData[item.key] = item.value;
      });
      return newListData;
    });
  };

  useEffect(() => {
    const fetchDetailData = async () => {
      setLoading(true)
      await getEmployeeManagementDetail();
      setLoading(false)
    }
    fetchDetailData()
  }, []);

  return (
    <Box
      padding="54px 40px 40px"
      display="flex"
      flexDirection="column"
      gap="42px"
      height="90vh"
      sx={{ overflowY: 'scroll' }}
    >
      {/* MORE INFORMATION */}
      <Typography variant="h6" color="var(--base-black)" textAlign="center">
        상세 정보
      </Typography>
      <Box display="flex" flexDirection="column" gap="30px" position="relative">
        <Box display="flex" flexDirection="column">
          <Box display="flex" flexDirection="column" gap={5}>
            {/* BASIC INFORMATION */}
            <FieldBox title="기본 정보">
              {/* 1. SUBSCRIPTION CATEGORY */}
              <FieldBoxProperty
                isLoading={isLoading}
                name="가입구분"
                type="text"
                value={
                  (employeeData?.staffMstDetail?.joinTp === 'markany' &&
                    '마크애니') ||
                  (employeeData?.staffMstDetail?.joinTp === 'google' &&
                    'Google') ||
                  (employeeData?.staffMstDetail?.joinTp === 'kakao' &&
                    '카카오') ||
                  '--'
                }
              />
              {/* 2. ID */}
              <FieldBoxProperty
                isLoading={isLoading}
                name="아이디"
                type="text"
                value={employeeData?.staffMstDetail?.userUid ?? '--'}
              />
              {/* 3. NAME */}
              <FieldBoxProperty
                isLoading={isLoading}
                name="성명"
                type="text"
                value={employeeData?.staffMstDetail?.userName ?? '--'}
              />
              {/* 4. EMAIL */}
              <FieldBoxProperty
                isLoading={isLoading}
                name="이메일"
                type="text"
                value={employeeData?.staffMstDetail?.userEmail ?? '--'}
              />
              {/* 5. EMPLOYEE STATUS */}
              <FieldBoxProperty
                isLoading={isLoading}
                name="직원 상태"
                type="text"
                value={
                  (employeeData?.staffMstDetail?.userSt === '10' && '신청') ||
                  (employeeData?.staffMstDetail?.userSt === '50' && '정상') ||
                  (employeeData?.staffMstDetail?.userSt === '80' && '일시정지') ||
                  (employeeData?.staffMstDetail?.userSt === '90' && '퇴사') ||
                  '--'
                }
              />
            </FieldBox>

            {/* REGISTRANT INFORMATION*/}
            <FieldBox title="등록자 정보">
              {/* 1. REGISTRANT TYPE */}
              <FieldBoxProperty
                isLoading={isLoading}
                name="등록자 유형"
                type="text"
                value={
                  (employeeData?.staffMstDetail?.regTp === 'admin' &&
                    '관리자') ||
                  (employeeData?.staffMstDetail?.regTp === 'staff' && '직원') ||
                  '--'
                }
              />
              {/* 2. REGISTRANT NAME */}
              <FieldBoxProperty
                isLoading={isLoading}
                name="등록자"
                type="text"
                value={employeeData?.staffMstDetail?.regName ?? '--'}
              />
              {/* 3. REGISTRATION DATE & TIME */}
              <FieldBoxProperty
                isLoading={isLoading}
                name="등록 일시"
                type="text"
                value={employeeData?.staffMstDetail?.regDt ?? '--'}
              />
              {/* 4. MODIFICATION TYPE */}
              <FieldBoxProperty
                isLoading={isLoading}
                name="수정 유형"
                type="text"
                value={
                  (employeeData?.staffMstDetail?.updTp === 'admin' &&
                    '관리자') ||
                  (employeeData?.staffMstDetail?.updTp === 'staff' && '직원') ||
                  '--'
                }
              />
              {/* 5. MODIFIER */}
              <FieldBoxProperty
                isLoading={isLoading}
                name="수정자"
                type="text"
                value={employeeData?.staffMstDetail?.updName ?? '--'}
              />
              {/* 6. DATE OF MODIFICATION */}
              <FieldBoxProperty
                isLoading={isLoading}
                name="수정 일시"
                type="text"
                value={employeeData?.staffMstDetail?.updDt ?? '--'}
              />
            </FieldBox>
          </Box>
        </Box>
        <Box
          display="flex"
          flexDirection="row"
          justifyContent="center"
        // gap="14px"
        >
          {/* <Button variant="outlined" color="secondary">
            삭제하기
          </Button> */}
          <Link
            state={employeeData}
            to={{
              pathname: `${ROUTES.EMPLOYEE_MANAGEMENT.UPDATE_FILE.path}/${employeeData?.staffMstDetail?.userSeq}`,
            }}
          >
            <Button variant="contained" color="primary" sx={{ width: '112px' }}>
              수정하기
            </Button>
          </Link>
        </Box>
      </Box>
    </Box>
  );
};

export default EmployeeManagementDetail;
